import { useCallback, useEffect, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { Widget } from "@/modules";
import { useEditWidgetWebSocket } from "@/modules/websocket/hooks/useEditWidgetWebSocket";
import { useEditWidgetV2WebSocket } from "@/modules/websocket/hooks/useEditWidgetV2WebSocket";
import { EditWidgetV2WebSocketMessage, EditClarificationItem } from "@/modules/websocket/types";
import { AgentSelectionContext } from "@/modules/websocket/types/agent.types";
import { useWidget } from "../context/WidgetContext";
import { widgetEndpoints } from "@/modules/widgets/api/endpoints";
import { config } from "@/config/env";

function buildSelectionContext(answers: { title: string; values: string[] }[]): AgentSelectionContext {
    const ctx: AgentSelectionContext = {};
    for (const ans of answers) {
        const t = ans.title.toLowerCase();
        if (t.includes("product")) ctx.product = ans.values;
        else if (t.includes("level")) ctx.level = ans.values[0];
        else if (t.includes("segment")) ctx.segment = ans.values[0];
        else if (t.includes("metric")) ctx.metrics = ans.values;
    }
    return ctx;
}

interface UseEditWidgetViaAIParams {
    widget: Widget | null;
    onWidgetUpdated?: (widget: Widget) => void;
    onClose: () => void;
}

export const useEditWidgetViaAI = ({ widget, onWidgetUpdated, onClose }: UseEditWidgetViaAIParams) => {
    const { dispatch } = useWidget();

    // Both hooks must always be called — React forbids conditional hook calls.
    const v1 = useEditWidgetWebSocket();
    const v2 = useEditWidgetV2WebSocket();
    const { isReady, isProcessing, messages, sendRequest, clearSessionMessages, resetProcessing } =
        config.AGENT_V2_ENABLED ? v2 : v1;

    const [prompt, setPrompt] = useState("");
    const [progressSteps, setProgressSteps] = useState<string[]>([]);
    const [feedbackMessage, setFeedbackMessage] = useState<string | undefined>(undefined);
    const [previewWidget, setPreviewWidget] = useState<Widget | null>(null);
    const [isAccepting, setIsAccepting] = useState(false);
    const [clarificationItems, setClarificationItems] = useState<EditClarificationItem[] | undefined>(undefined);
    const accumulatedAnswers = useRef<{ title: string; values: string[] }[]>([]);

    const widgetUpdated = useRef(false);
    const clearSessionMessagesRef = useRef(clearSessionMessages);
    clearSessionMessagesRef.current = clearSessionMessages;
    const resetProcessingRef = useRef(resetProcessing);
    resetProcessingRef.current = resetProcessing;

    useEffect(() => {
        const lastMessage = messages[messages.length - 1] as unknown as EditWidgetV2WebSocketMessage | undefined;
        if (!lastMessage) return;

        const { data } = lastMessage;

        if (data.type === "step") {
            setProgressSteps((prev) => [...prev, (data as any).step]);
        } else if (data.type === "clarification") {
            resetProcessingRef.current();
            clearSessionMessagesRef.current();
            const items = (data as any).items as EditClarificationItem[] | undefined;
            if (items?.length) {
                setClarificationItems(items);
            } else {
                setFeedbackMessage((data as any).message);
            }
        } else if (data.type === "preview" && !widgetUpdated.current) {
            // Preview mode — backend skipped the DB save, show the proposed widget for confirmation
            widgetUpdated.current = true;
            resetProcessingRef.current();
            clearSessionMessagesRef.current();
            if ((data as any).selectionContext && Object.keys((data as any).selectionContext).length > 0) {
                console.log("[Agent] selectionContext:", (data as any).selectionContext);
            }
            setPreviewWidget((data as any).widget as Widget);
        } else if (data.type === "done" && !widgetUpdated.current) {
            widgetUpdated.current = true;
            resetProcessingRef.current();
            clearSessionMessagesRef.current();
            if ((data as any).selectionContext && Object.keys((data as any).selectionContext).length > 0) {
                console.log("[Agent] selectionContext:", (data as any).selectionContext);
            }
            dispatch({ type: "UPDATE_WIDGET", payload: (data as any).widget });
            if (onWidgetUpdated) {
                onWidgetUpdated((data as any).widget);
            }
            onClose();
        } else if (data.type === "error") {
            resetProcessingRef.current();
            clearSessionMessagesRef.current();
            setFeedbackMessage((data as any).error || "Something went wrong. Please try again.");
        }
    }, [messages, dispatch, onWidgetUpdated, onClose]);

    const handleSubmit = useCallback(async () => {
        const trimmedPrompt = prompt.trim();
        if (!trimmedPrompt || !widget || !isReady) return;

        widgetUpdated.current = false;
        setFeedbackMessage(undefined);
        setProgressSteps([]);
        setPreviewWidget(null);
        setClarificationItems(undefined);
        accumulatedAnswers.current = [];

        try {
            sendRequest(widget, trimmedPrompt);
        } catch (err) {
            resetProcessingRef.current();
            console.error("Failed to send edit-widget request:", err);
        }
    }, [prompt, widget, isReady, sendRequest]);

    const handleClarificationConfirm = useCallback((answers: { title: string; values: string[] }[]) => {
        if (!widget || !isReady) return;

        // Merge new answers with accumulated ones (replace same-title entries)
        const merged = [...accumulatedAnswers.current];
        for (const a of answers) {
            const idx = merged.findIndex((x) => x.title === a.title);
            if (idx >= 0) merged[idx] = a; else merged.push(a);
        }
        accumulatedAnswers.current = merged;
        const ctx = buildSelectionContext(merged);

        setClarificationItems(undefined);
        setProgressSteps([]);
        setFeedbackMessage(undefined);

        try {
            sendRequest(widget, prompt.trim(), merged, ctx);
        } catch (err) {
            resetProcessingRef.current();
            console.error("Failed to send clarification answers:", err);
        }
    }, [widget, isReady, prompt, sendRequest]);

    const handlePreviewAccept = useCallback(async () => {
        if (!previewWidget) return;
        setIsAccepting(true);
        try {
            const result = await widgetEndpoints.updateWidget(previewWidget);
            const saved = result.widget ?? previewWidget;
            dispatch({ type: "UPDATE_WIDGET", payload: saved });
            if (onWidgetUpdated) onWidgetUpdated(saved);
            setPreviewWidget(null);
            onClose();
        } catch (err) {
            console.error("Failed to save edited widget:", err);
            setFeedbackMessage("Failed to save changes. Please try again.");
        } finally {
            setIsAccepting(false);
        }
    }, [previewWidget, dispatch, onWidgetUpdated, onClose]);

    const handlePreviewDiscard = useCallback(() => {
        setPreviewWidget(null);
        widgetUpdated.current = false;
    }, []);

    const handleRefineSubmit = useCallback((refineText: string) => {
        if (!refineText.trim() || !widget || !isReady) return;

        widgetUpdated.current = false;
        setFeedbackMessage(undefined);
        setProgressSteps([]);
        setPreviewWidget(null);
        setClarificationItems(undefined);
        accumulatedAnswers.current = [];
        setPrompt(refineText);

        try {
            sendRequest(widget, refineText);
        } catch (err) {
            resetProcessingRef.current();
            console.error("Failed to send refine request:", err);
        }
    }, [widget, isReady, sendRequest]);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void handleSubmit();
            }

            if (event.key === "Escape") {
                onClose();
            }
        },
        [handleSubmit, onClose]
    );

    const reset = useCallback(() => {
        setPrompt("");
        setProgressSteps([]);
        setFeedbackMessage(undefined);
        setPreviewWidget(null);
        setIsAccepting(false);
        setClarificationItems(undefined);
        accumulatedAnswers.current = [];
        widgetUpdated.current = false;
        clearSessionMessagesRef.current();
    }, []);

    const isDisabled = !!previewWidget || !!clarificationItems?.length || !prompt.trim() || isProcessing || !isReady;

    return {
        prompt,
        setPrompt,
        handleSubmit,
        handleKeyDown,
        isProcessing,
        isDisabled,
        progressSteps,
        feedbackMessage,
        previewWidget,
        isAccepting,
        handlePreviewAccept,
        handlePreviewDiscard,
        handleRefineSubmit,
        clarificationItems,
        handleClarificationConfirm,
        reset,
    };
};
